import { SocialNetworksEnum } from '../../../constants/social-networks';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import { Network, SocialOAuthProvider, SocialProfileFieldsFragment, ThirdPartyProfile } from '../../../models/graphql';
import { FormElementValueType } from '../../../models/basic-objects';
import { getUrlParams } from '../../../helpers/getUrlParams';
import { BadgeViewProps } from '../../../components/containers/Badge/Badge.view';
import uuidGen from '../../../helpers/uuidGen';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../constants/badge';

export const mapTypeNameToSelectItem = (name: string): SelectItem => {
  let item: SelectItem = { label: '', value: '' };

  switch (name) {
    case 'FacebookSocialProfile':
      item = {
        label: 'Facebook',
        value: SocialNetworksEnum.facebook
      };
      break;
    case 'InstagramSocialProfile':
      item = {
        label: 'Instagram',
        value: SocialNetworksEnum.instagram
      };
      break;
    case 'LinkedInSocialProfile':
      item = {
        label: 'LinkedIn',
        value: SocialNetworksEnum.linkedin
      };
      break;
    case 'PinterestSocialProfile':
      item = {
        label: 'Pinterest',
        value: SocialNetworksEnum.pinterest
      };
      break;
    case 'TwitterSocialProfile':
      item = {
        label: 'X (Twitter)',
        value: SocialNetworksEnum.twitter
      };
      break;
  }

  return item;
};

export type PlatformListItemsType = {
  type: string;
  selectItem: SelectItem;
};

export const _getPlatformListItems = (profiles: SocialProfileFieldsFragment[]): SelectItem[] => {
  const platformListItems: PlatformListItemsType[] = [];

  profiles.forEach(p => {
    if (!platformListItems.find(item => item.type === p.__typename)) {
      if (!p.__typename) return;

      const listItem: PlatformListItemsType = {
        type: p.__typename,
        selectItem: mapTypeNameToSelectItem(p.__typename)
      };
      platformListItems.push(listItem);
    }
  });

  return platformListItems.map(p => p.selectItem);
};

export const mergeAndSortHistoryItems = (
  profileSearchHistory: ThirdPartyProfile[],
  // items: SelectItem[],
  items: { label: string; value: string }[],
  key: keyof ThirdPartyProfile
): { label: string; value: string }[] => {
  const historyItems = profileSearchHistory.map(p => {
    const value = p[key];
    if (typeof value === 'undefined' || value === null) {
      throw new Error(`Key "${String(key)}" does not exist on profile item or is null/undefined.`);
    }
    return {
      label: p.displayName,
      value: String(value)
    };
  });

  // Create a map to ensure unique items based on the 'value' key
  const uniqueItemsMap = new Map<string, { label: string; value: string }>();

  items.forEach(item => uniqueItemsMap.set(item.value, item));
  historyItems.forEach(item => uniqueItemsMap.set(item.value, item));

  const mergedItems = Array.from(uniqueItemsMap.values());

  // Sort the merged items by 'label'
  return mergedItems.sort((a, b) => a.label.localeCompare(b.label));
};

export const generateProfileList = (
  profiles: ThirdPartyProfile[],
  history: ThirdPartyProfile[],
  autoCompleteValue: FormElementValueType,
  elementValue: string,
  idKey: keyof ThirdPartyProfile
) => {
  const items = profiles.map(p => ({ label: p.displayName, value: String(p[idKey]) }));

  if (!elementValue) {
    return mergeAndSortHistoryItems(history, items, idKey);
  }

  if (typeof autoCompleteValue === 'string' && autoCompleteValue.toLowerCase() === elementValue.toLowerCase()) {
    const matchedItem = items.find(item => item.value === autoCompleteValue);

    if (!matchedItem) {
      const historyItem = history.find(p => p.username === autoCompleteValue);

      if (historyItem) {
        return [{ label: historyItem.displayName, value: String(historyItem[idKey]) }];
      }
    }
  }

  // Sort items by 'label' before returning
  return items.sort((a, b) => a.label.localeCompare(b.label));
};

// utility for extracting and formatting the LinkUrl details
export const extractLinkUrlDetails = (linkUrl: string) => {
  const url = getUrlParams(linkUrl.trim());

  if (!url) return null;

  const domain = url.hostname.replace(/^www\./, '');
  const path = (url.pathname + url.search).trim();

  return { url, domain, path };
};

// helper to build badge props
export const buildBadgeProps = (
  urlDetails: { url: URL; domain: string; path: string },
  truncateLinkUrl: boolean,
  hasTooltipBadge: boolean
): BadgeViewProps => {
  const { url, domain, path } = urlDetails;
  const text = truncateLinkUrl ? path || domain : domain + path;

  return hasTooltipBadge
    ? {
        tooltipText: url.href,
        badgeKey: uuidGen(),
        color: BadgeColorsEnum.PRIMARY,
        size: BadgeSizesEnum.MEDIUM,
        text,
        shrinkContent: true,
        clickHandler: () => window.open(url.href, '_blank', 'noopener,noreferrer')
      }
    : {
        color: BadgeColorsEnum.PRIMARY,
        size: BadgeSizesEnum.MEDIUM,
        text,
        shrinkContent: true,
        clickHandler: () => window.open(url.href, '_blank', 'noopener,noreferrer')
      };
};

export const isSocialOAuthProviderKey = (value: string): value is keyof typeof SocialOAuthProvider => {
  return Object.keys(SocialOAuthProvider).includes(value);
};

export const isNetworkKey = (value: string): value is keyof typeof Network => {
  return Object.keys(Network).includes(value);
};

export const mapValueToSocialOAuthProvider = (
  value: string | FormElementValueType | null
): SocialOAuthProvider | null => {
  if (!value || typeof value !== 'string') return null;

  const upperCasedValue = value.toUpperCase();

  if (isSocialOAuthProviderKey(upperCasedValue)) {
    return SocialOAuthProvider[upperCasedValue];
  }

  return null;
};

export const mapValueToNetwork = (value: string | FormElementValueType | null): Network | null => {
  if (!value || typeof value !== 'string') return null;

  const upperCasedValue = value.toUpperCase();

  if (isNetworkKey(upperCasedValue)) {
    return Network[upperCasedValue];
  }

  return null;
};
